import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FORMS_API_URL } from '../utils/formSessionApi';
import { getContractApiUrl } from '../utils/apiUrls';

const BACKEND_URL = getContractApiUrl();

async function fetchClientSuggestions({
  clientSide,
  query,
  formSessionToken,
  userEmail,
}) {
  const params = new URLSearchParams({ client_side: clientSide, q: query || '' });
  const url = formSessionToken
    ? `${FORMS_API_URL}/sessions/${formSessionToken}/client-suggestions?${params}`
    : `${BACKEND_URL}/wizard/client-suggestions?${params}&user_email=${encodeURIComponent(userEmail)}`;

  const res = await fetch(url);
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.message || data.error || 'Failed to load client suggestions');
  }
  return data.suggestions || [];
}

const DEAL_LINK_KEYS = [
  'wizard_client_label_buyer',
  'wizard_client_label_seller',
  'buyer_agreement_id',
  'listing_id',
  'buyer_name',
  'seller_name',
  'buyer_email',
  'seller_email',
  'buyers',
  'sellers',
  'property_address',
  'property_address_city',
  'property_address_state',
  'property_address_zip_code',
  'property_address_county',
  'mls_number',
  'year_built',
  'legal_description',
  'purchase_price',
  'listing_price',
  'buyer_agent_name',
  'buyer_agent_email',
  'buyer_agent_phone',
  'buyer_agent_brokerage',
  'sellers_agent_name',
  'sellers_agent_email',
  'sellers_agent_phone',
  'sellers_agent_brokerage',
];

const LISTING_OWNED_KEYS = new Set([
  'listing_id',
  'wizard_client_label_seller',
  'seller_name',
  'seller_email',
  'sellers',
  'property_address',
  'property_address_city',
  'property_address_state',
  'property_address_zip_code',
  'property_address_county',
  'mls_number',
  'year_built',
  'legal_description',
  'purchase_price',
  'listing_price',
]);

const BUYER_OWNED_KEYS = new Set([
  'buyer_agreement_id',
  'wizard_client_label_buyer',
  'buyer_name',
  'buyer_email',
  'buyers',
]);

const suggestionToContractPatch = (suggestion) => {
  if (!suggestion) return {};
  const patch = {};
  if (suggestion.entity_type === 'buyer_agreement') {
    patch.wizard_client_label_buyer = suggestion.label;
    patch.buyer_agreement_id = suggestion.buyer_agreement_id;
    if (suggestion.buyer_name) patch.buyer_name = suggestion.buyer_name;
    if (suggestion.buyer_email) patch.buyer_email = suggestion.buyer_email;
    if (Array.isArray(suggestion.buyers)) patch.buyers = suggestion.buyers;
    if (suggestion.buyer_agent_name) patch.buyer_agent_name = suggestion.buyer_agent_name;
    if (suggestion.buyer_agent_email) patch.buyer_agent_email = suggestion.buyer_agent_email;
    if (suggestion.buyer_agent_phone) patch.buyer_agent_phone = suggestion.buyer_agent_phone;
    if (suggestion.buyer_agent_brokerage) patch.buyer_agent_brokerage = suggestion.buyer_agent_brokerage;
  }
  if (suggestion.entity_type === 'listing') {
    patch.wizard_client_label_seller = suggestion.label;
    patch.listing_id = suggestion.listing_id;
    if (suggestion.seller_name) patch.seller_name = suggestion.seller_name;
    if (suggestion.seller_email) patch.seller_email = suggestion.seller_email;
    if (Array.isArray(suggestion.sellers)) patch.sellers = suggestion.sellers;
    if (suggestion.property_address) patch.property_address = suggestion.property_address;
    if (suggestion.property_address_city) patch.property_address_city = suggestion.property_address_city;
    if (suggestion.property_address_state) patch.property_address_state = suggestion.property_address_state;
    if (suggestion.property_address_zip_code) {
      patch.property_address_zip_code = suggestion.property_address_zip_code;
    }
    if (suggestion.property_address_county) {
      patch.property_address_county = suggestion.property_address_county;
    }
    if (suggestion.mls_number) patch.mls_number = suggestion.mls_number;
    if (suggestion.year_built) patch.year_built = suggestion.year_built;
    if (suggestion.legal_description) patch.legal_description = suggestion.legal_description;
    if (suggestion.listing_price) patch.listing_price = suggestion.listing_price;
    if (suggestion.purchase_price) patch.purchase_price = suggestion.purchase_price;
    if (suggestion.sellers_agent_name) patch.sellers_agent_name = suggestion.sellers_agent_name;
    if (suggestion.sellers_agent_email) patch.sellers_agent_email = suggestion.sellers_agent_email;
    if (suggestion.sellers_agent_phone) patch.sellers_agent_phone = suggestion.sellers_agent_phone;
    if (suggestion.sellers_agent_brokerage) {
      patch.sellers_agent_brokerage = suggestion.sellers_agent_brokerage;
    }
  }
  return patch;
};

export const resetDealLinkFields = (data) => {
  const next = { ...data };
  DEAL_LINK_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
};

const clearDealLink = (data, clientSide) => {
  const next = { ...data };
  DEAL_LINK_KEYS.forEach((key) => {
    if (
      clientSide === 'buyer'
      && (LISTING_OWNED_KEYS.has(key) || key.startsWith('sellers_agent'))
    ) {
      return;
    }
    if (
      clientSide === 'seller'
      && (BUYER_OWNED_KEYS.has(key) || key.startsWith('buyer_agent'))
    ) {
      return;
    }
    delete next[key];
  });
  return next;
};

const WizardClientPicker = ({
  clientSide,
  contractData,
  setContractData,
  formSessionToken = null,
  userEmail = null,
  placeholder = 'Start typing to search…',
  freeTextField = clientSide === 'buyer' ? 'buyer_name' : 'seller_name',
  mode = 'deal',
  emptyState = null,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [query, setQuery] = useState('');
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  const selectedId = clientSide === 'buyer'
    ? contractData.buyer_agreement_id
    : contractData.listing_id;
  const labelKey = clientSide === 'buyer' ? 'wizard_client_label_buyer' : 'wizard_client_label_seller';
  const selectedLabel = selectedId
    ? (contractData[labelKey] || contractData[freeTextField] || '')
    : '';

  const loadSuggestions = useCallback(async (nextQuery) => {
    if (!formSessionToken && !userEmail) {
      setSuggestions([]);
      return;
    }
    setLoadingSuggestions(true);
    try {
      const rows = await fetchClientSuggestions({
        clientSide,
        query: nextQuery,
        formSessionToken,
        userEmail,
      });
      setSuggestions(rows);
      setShowSuggestions(true);
      setActiveSuggestion(-1);
    } catch (err) {
      console.error('Client autocomplete failed:', err);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoadingSuggestions(false);
    }
  }, [clientSide, formSessionToken, userEmail]);

  useEffect(() => {
    loadSuggestions('');
  }, [loadSuggestions]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const applySuggestion = (suggestion) => {
    setContractData((prev) => {
      const currentId = clientSide === 'buyer'
        ? prev.buyer_agreement_id
        : prev.listing_id;
      const base = mode === 'deal' && currentId
        ? clearDealLink(prev, clientSide)
        : prev;
      const patch = suggestionToContractPatch(suggestion);
      if (base.purchase_price) {
        delete patch.purchase_price;
      }
      return {
        ...base,
        ...patch,
      };
    });
    setQuery('');
    setShowSuggestions(false);
    setActiveSuggestion(-1);
  };

  const handleChange = (nextValue) => {
    setQuery(nextValue);
    if (mode === 'deal' && selectedId && nextValue.trim() !== String(selectedLabel || '').trim()) {
      setContractData((prev) => clearDealLink(prev, clientSide));
    }
    if (mode !== 'deal') {
      setContractData((prev) => ({
        ...prev,
        [freeTextField]: nextValue,
        [labelKey]: nextValue,
      }));
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadSuggestions((nextValue || '').trim());
    }, 200);
    setShowSuggestions(true);
  };

  const handleKeyDown = (event) => {
    if (!showSuggestions || !suggestions.length) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveSuggestion((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveSuggestion((prev) => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter' && activeSuggestion >= 0) {
      event.preventDefault();
      applySuggestion(suggestions[activeSuggestion]);
    } else if (event.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const inputValue = selectedId && !query ? selectedLabel : query;
  const defaultEmpty = clientSide === 'buyer'
    ? 'No fully executed buyer agreements are available. Finish a BBSA first, or pick a buyer who has no contract or an active one.'
    : 'No active listings found. Create a listing agreement first.';

  return (
    <div className="wizard-client-picker" ref={containerRef}>
      <div className="wizard-input-group wizard-agent-name-autocomplete">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => {
            setShowSuggestions(true);
            if (!suggestions.length) loadSuggestions((query || '').trim());
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="wizard-input"
          autoComplete="off"
        />
        {loadingSuggestions && (
          <div className="wizard-agent-suggest-status">Loading your deals…</div>
        )}
        {!loadingSuggestions && showSuggestions && suggestions.length === 0 && (
          <div className="wizard-agent-suggest-status">{emptyState || defaultEmpty}</div>
        )}
        {showSuggestions && suggestions.length > 0 && (
          <ul className="wizard-agent-suggest-list" role="listbox">
            {suggestions.map((suggestion, index) => (
              <li key={`${suggestion.entity_type}-${suggestion.id}`}>
                <button
                  type="button"
                  role="option"
                  aria-selected={index === activeSuggestion}
                  className={`wizard-agent-suggest-item${index === activeSuggestion ? ' active' : ''}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => applySuggestion(suggestion)}
                >
                  <span className="wizard-agent-suggest-name">{suggestion.label}</span>
                  {suggestion.property_address && (
                    <span className="wizard-agent-suggest-meta">{suggestion.property_address}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default WizardClientPicker;
export { suggestionToContractPatch, fetchClientSuggestions, clearDealLink };
